import { servicePost } from "./client.ts";
import type { DataSource, GridCount, InputParam, SortOrder, ExportDataFileType, ExportInfo, ScheduledExport } from "../types/grid.ts";
import type { KioskModel, KioskCommandResult, KioskCommandType, OperatorModel, TransactionModel } from "../types/models.ts";
import { getClientTimeZone } from "../util/clientEnv.ts";
import { GRID_DATA_VERSION } from "../util/gridDataParser.ts";

export function getDataGrid(
  name: string,
  list: InputParam[],
  orderList: SortOrder[],
  iIndexStart: number,
  iIndexEnd: number,
  // Column-exclusion wire protocol (Spec C §6.3): omitted = report default (server may
  // prune to its keep-set); full list = user-changed columns (server expands with deps).
  columnList?: string[] | null,
  // Aborting it also stops the query: the server cancels it when the request goes away.
  signal?: AbortSignal,
) {
  iIndexStart+=1;
  iIndexEnd +=1;
  return servicePost<DataSource>("GetDataGrid", {
    name, list, orderList, iIndexStart, iIndexEnd,
    ...(columnList && columnList.length > 0 ? { columnList } : {}),
    dataVersion: GRID_DATA_VERSION,
  }, signal);
}

export function getDataGridCount(name: string, list: InputParam[], signal?: AbortSignal) {
  return servicePost<GridCount>("GetDataGridCount2", { name, list }, signal);
}

/** Asks a running export of the caller's to stop; its status becomes Cancelled within seconds. */
export function cancelExport(exportId: string) {
  return servicePost<boolean>("CancelExport", { exportId });
}

export function getDataGridHeader(name: string) {
  return servicePost<DataSource>("GetDataGridHeader", { name });
}

export function updateGridCell(name: string, list: InputParam[]) {
  return servicePost("UpdateGridCell", { name, filter: list });
}

export function deleteGridCell(name: string, list: InputParam[]) {
  return servicePost<boolean>("DeleteGridCell", { name, filter: list });
}

export function executeStoredProcedure(sName: string, list: InputParam[], iQID: number) {
  return servicePost("WebExecuteSP", { sName, list, iQID });
}

export function exportDataGrid(args: {
  Name: string;
  FilterList: InputParam[];
  OrderList: SortOrder[];
  Culture: string;
  Timezone?: string;
  FileType: ExportDataFileType;
  ColumnList?: string[];
}) {
  if (!args.Timezone) args.Timezone = getClientTimeZone();
  return servicePost<ExportInfo>("ExportDataGridA", { args });
}

// Single "export in import format" entry point for every exportable Network Import
// card (Product Total, Generic Price, Locations, Kiosk Staff, NeoRFID, Serialnumber).
// The server resolves the import from the page's backing RealName and emits the
// import's column shape so customers can export -> edit -> re-import (round-trip safe).
// Same async pipeline as exportDataGrid (poll getExportStatus). OperatorID/DefaultLevel
// only apply to the Product Total operator picker; the other imports derive scope
// server-side from the caller and ignore them.
export function exportImportFile(args: {
  Name: string;
  Culture: string;
  Timezone?: string;
  FileType: ExportDataFileType;
  /** Operator-scoped imports: operator chosen in the picker by top-level users. */
  OperatorID?: number | null;
  /** Operator-scoped imports: export the default level (OperatorID IS NULL) records. */
  DefaultLevel?: boolean;
  /** GDPR-bearing imports: caller acknowledged personal-data handling -> export real PII (unmasked). */
  AcknowledgeGdpr?: boolean;
}) {
  if (!args.Timezone) args.Timezone = getClientTimeZone();
  return servicePost<ExportInfo>("ExportImportFile", { args });
}

export function getExportStatus(id: string) {
  return servicePost<ExportInfo>("GetExportStatus", { id });
}

export function getSimpleKioskData(reference: string, isStandalone: boolean) {
  return servicePost<KioskModel>("GetSimpleKioskData", { reference, isStandalone });
}

export function saveSimpleKioskData(data: KioskModel) {
  return servicePost<KioskModel>("SaveSimpleKioskData", { data });
}

export function simpleKioskCommand(
  KioskReference: string,
  Command: KioskCommandType,
  Force: boolean
) {
  return servicePost<KioskCommandResult>("SimpleKioskCommand", {
    args: { KioskReference, Command, Force },
  });
}

export function getSimpleOperatorData(reference: string, isStandalone: boolean) {
  return servicePost<OperatorModel>("GetSimpleOperatorData", { reference, isStandalone });
}

export function saveSimpleOperatorData(data: OperatorModel) {
  return servicePost<OperatorModel>("SaveSimpleOperatorData", { data });
}

export function getSimpleTransactionData(reference: string, isStandalone: boolean) {
  return servicePost<TransactionModel>("GetSimpleTransactionData", { reference, isStandalone });
}

export function getAppData(name: string) {
  return servicePost<string>("GetAppData", { name });
}

export function getInstantAppData(iFilter: number) {
  return servicePost<string>("GetInstantAppData", { iFilter });
}

export function saveAppConfig(apps: string[], nrOfColumns: number) {
  return servicePost("SaveAppConfig", { apps, nrOfColumns });
}

export function setScheduledExport(item: ScheduledExport) {
  return servicePost<ScheduledExport>("Set_ScheduledExport", { item });
}
